import React from "react";
import styles from "./EntriesTable.module.css";
import ResizableColumn from "../ResizableColumn";
import wordIcon from "../../../../assets/applications/WordIcon.svg";
import adobeIcon from "../../../../assets/applications/AdobeIcon.svg";
import outlookIcon from "../../../../assets/applications/OutlookIcon.svg";
import powerpointIcon from "../../../../assets/applications/PowerpointIcon.svg";
import excelIcon from "../../../../assets/applications/ExcelIcon.svg";
import zoomIcon from "../../../../assets/applications/ZoomIcon.svg";
import chromeIcon from "../../../../assets/applications/ChromeIcon.svg";
import teamsIcon from "../../../../assets/applications/TeamsIcon.svg";
import calendarIcon from "../../../../assets/applications/CalendarIcon.svg";
import kofaxIcon from "../../../../assets/applications/KofaxIcon.svg";
import foxitIcon from "../../../../assets/applications/FoxitIcon.svg";
import photosIcon from "../../../../assets/applications/WindowsPhotos.svg";
import combineIcon from "../../../../assets/applications/CombineIcon.svg";
import combineIconWhite from "../../../../assets/applications/CombineIconWhite.svg";
import duplicateIcon from "../../../../assets/applications/DuplicateIcon.svg";
import duplicateIconWhite from "../../../../assets/applications/DuplicateIconWhite.svg";
import phoneIcon from "../../../../assets/icons/phone.svg";
import phoneWhiteIcon from "../../../../assets/icons/phoneWhite.svg";
import userInputIcon from "../../../../assets/icons/userInput.svg";
import userInputWhiteIcon from "../../../../assets/icons/userInputWhite.svg";
import { useDarkMode } from "@/shared/hooks/useDarkMode";
import { isActivityRequired, isTaskRequired } from "@/shared/utils/firm";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { AlertTriangle } from "lucide-react";

export type EntryStatus = "tracked" | "exported" | "deleted";

export type Entry = {
  id: number;
  date: string;
  matter: string;
  matter_id?: string;
  matter_name?: string;
  client?: string;
  description: string;
  task: string;
  activity?: string;
  document_name?: string;
  duration: number;
  end_time?: string;
  application?: string;
  /** Status for admin view badge display (tracked/exported/deleted) */
  status?: EntryStatus;
  /** Admin feedback on narrative - shows warning indicator when present */
  attorney_feedback?: string | null;
};

export type ColumnWidths = {
  date: number;
  matter: number;
  codes: number;
  narrative: number;
  units: number;
};

export type SortingMode = "time" | "matter";

export type Matter = {
  matter_id: string;
  task_group: string;
  activity_group: string;
};

export interface EntriesTableProps {
  darkMode?: boolean;
  entries: Entry[];
  // exportedEntries?: Entry[];
  groupedEntries: Record<string, Entry[]>;
  // groupedExportedEntries?: Record<string, Entry[]>;
  columnWidths: ColumnWidths;
  onResizeColumn: (key: keyof ColumnWidths, width: number) => void;
  onCascadingResize?: (resizingColumn: keyof ColumnWidths, deltaPercentage: number) => void;
  checkedRows: Set<number>;
  selectAllChecked: boolean;
  selectAllIndeterminate: boolean;
  onToggleSelectAll: () => void;
  onToggleRow: (id: number) => void;
  onRowClick?: (entry: Entry) => void;
  onToggleSortingMode?: () => void;
  showCheckboxes?: boolean;
  mode?: "entries" | "history";
  matters?: Matter[];
  /** Padding inside resizable columns (px). Use for admin view to prevent content touching dividers. */
  cellPadding?: number;
  /** Current sorting mode - controls whether group totals are shown (only for "matter" mode) */
  sortingMode?: SortingMode;
}

const EntriesTable: React.FC<EntriesTableProps> = ({
  entries,
  groupedEntries,
  columnWidths,
  onResizeColumn,
  onCascadingResize,
  checkedRows,
  selectAllChecked,
  selectAllIndeterminate,
  onToggleSelectAll,
  onToggleRow,
  onRowClick,
  onToggleSortingMode,
  showCheckboxes = true,
  mode = "entries",
  matters = [],
  cellPadding = 0,
  sortingMode = "time",
}) => {
  const { darkMode } = useDarkMode();

  const renderAppIcon = (application?: string) => {
    switch (application) {
      case "Microsoft Word": return <img src={wordIcon} alt="Word" className={styles.appLogo} />;
      case "Microsoft Outlook": return <img src={outlookIcon} alt="Outlook" className={styles.appLogo} />;
      case "Microsoft Outlook Calendar": return <img src={calendarIcon} alt="Calendar" className={styles.appLogo} />;
      case "Adobe Acrobat": return <img src={adobeIcon} alt="Adobe" className={styles.appLogo} />;
      case "Microsoft Powerpoint": return <img src={powerpointIcon} alt="PowerPoint" className={styles.appLogo} />;
      case "Microsoft Excel": return <img src={excelIcon} alt="Excel" className={styles.appLogo} />;
      case "Zoom": return <img src={zoomIcon} alt="Zoom" className={styles.appLogo} />;
      case "Google Chrome": return <img src={chromeIcon} alt="Chrome" className={styles.appLogo} />;
      case "Microsoft Teams": return <img src={teamsIcon} alt="Teams" className={styles.appLogo} />;
      case "Phone": return <img src={darkMode ? phoneWhiteIcon : phoneIcon} alt="Phone" className={styles.appLogo} />;
      case "User Input": return <img src={darkMode ? userInputWhiteIcon : userInputIcon} alt="User" className={styles.appLogo} />;
      case "Kofax": return <img src={darkMode ? kofaxIcon : kofaxIcon} alt="Kofax" className={styles.appLogo} />;
      case "Foxit": return <img src={foxitIcon} alt="Foxit" className={styles.appLogo} />;
      case "Combine": return <img src={darkMode ? combineIconWhite : combineIcon} alt="Combine" className={styles.appLogo} />;
      case "Duplicate": return <img src={darkMode ? duplicateIconWhite : duplicateIcon} alt="Duplicate" className={styles.appLogo} />;
      case "Photos": return <img src={photosIcon} alt="Photos" className={styles.appLogo} />;
      default: return null;
    }
  };

  const isIncomplete = (e: Entry) => {
    const taskRequired = isTaskRequired(e.matter_id, matters);
    const activityRequired = isActivityRequired(e.matter_id, matters);
    return (
      !e.matter ||
      !e.description ||
      (taskRequired && !e.task) ||
      (activityRequired && !e.activity) ||
      e.description === "Please fill in the narrative."
    );
  };
  // const colKeys = ["date", "matter", "task", "description", "document"] as const;

  const renderHeader = () => (
    <div className={styles.headerRow}>
      <div className={styles.headerLeft}>
        <div className={styles.headerCheckbox}>
          {showCheckboxes && (
            <label className={styles.checkboxHitArea}>
              <input
                type="checkbox"
                checked={selectAllChecked}
                ref={(el) => {
                  if (el) el.indeterminate = selectAllIndeterminate;
                }}
                onChange={onToggleSelectAll}
              />
            </label>
          )}
        </div>
        <div className={styles.headerApp}></div>
        <ResizableColumn
          width={columnWidths.date}
          onResize={(w) => onResizeColumn("date", w)}
          onCascadingResize={(_, d) => onCascadingResize?.("date", d)}
          columnKey="date"
          className={styles.headerDate}
          contentPadding={cellPadding}
        >
          Date
        </ResizableColumn>
        <ResizableColumn
          width={columnWidths.matter}
          onResize={(w) => onResizeColumn("matter", w)}
          onCascadingResize={(_, d) => onCascadingResize?.("matter", d)}
          columnKey="matter"
          className={styles.headerMatter}
          onClick={onToggleSortingMode}
          contentPadding={cellPadding}
        >
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>Matter</span>
              </TooltipTrigger>
              <TooltipContent>
                <p>Click to sort</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </ResizableColumn>
        <ResizableColumn
          width={columnWidths.codes}
          onResize={(w) => onResizeColumn("codes", w)}
          onCascadingResize={(_, d) => onCascadingResize?.("codes", d)}
          columnKey="codes"
          className={styles.headerCodes}
          contentPadding={cellPadding}
        >
          Codes
        </ResizableColumn>
        <div className={styles.headerNarrative} style={{ width: `${columnWidths.narrative}%` }}>
          Narrative
        </div>
      </div>
      <div className={styles.headerUnitsWrapper}>
        <div style={{ width: "100%", textAlign: "right" }}>Units</div>
      </div>
    </div>
  );

  const renderRow = (entry: Entry) => (
    <div
      key={entry.id}
      className={styles.entryRow}
      onClick={(e) => {
        const target = e.target as HTMLElement;
        if (target.closest(".item-checkbox") || target.closest("label")) return;
        onRowClick?.(entry);
      }}
    >
      <div className={styles.entryLeft}>
        <div className={styles.entryCheckbox}>
          {showCheckboxes && (
            <label className={styles.checkboxHitArea}>
              <input
                className="item-checkbox"
                type="checkbox"
                checked={checkedRows.has(entry.id)}
                onChange={() => onToggleRow(entry.id)}
              />
            </label>
          )}
        </div>
        <div className={styles.entryApp}>
          {renderAppIcon(entry.application)}
        </div>
        <ResizableColumn
          width={columnWidths.date}
          onResize={(w) => onResizeColumn("date", w)}
          onCascadingResize={(_, d) => onCascadingResize?.("date", d)}
          columnKey="date"
          className={styles.entryDate}
          contentPadding={cellPadding}
        >
          {entry.date}
        </ResizableColumn>
        <ResizableColumn
          width={columnWidths.matter}
          onResize={(w) => onResizeColumn("matter", w)}
          onCascadingResize={(_, d) => onCascadingResize?.("matter", d)}
          columnKey="matter"
          className={styles.matterWrapper}
          allowWrap
          contentPadding={cellPadding}
        >
          <div>
            {entry.matter_name}{entry.matter ? ` - ${entry.matter}` : ""}
          </div>
          {entry.client && (
            <div style={{ opacity: 0.7, fontSize: "0.9em" }}>
              {entry.client}
            </div>
          )}
        </ResizableColumn>
        <ResizableColumn
          width={columnWidths.codes}
          onResize={(w) => onResizeColumn("codes", w)}
          onCascadingResize={(_, d) => onCascadingResize?.("codes", d)}
          columnKey="codes"
          className={styles.codesWrapper}
          allowWrap
          contentPadding={cellPadding}
        >
          <div>{entry.task || ""}</div>
          {entry.activity && (
            <div>{entry.activity}</div>
          )}
        </ResizableColumn>
        <div className={styles.narrative} style={{ width: `${columnWidths.narrative}%` }}>
          {entry.attorney_feedback && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className={styles.feedbackWarning}>
                    <AlertTriangle className={styles.warningIcon} />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>This narrative has unresolved feedback</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {entry.description}
        </div>
      </div>
      <div className={styles.unitsWrapper}>
        {/* Status badge logic:
            - If entry.status is set (admin view), show status-based badge
            - Otherwise, use mode-based logic (attorney view) */}
        {entry.status ? (
          <>
            {entry.status === "exported" && <div className={styles.exportedBadge}>Exported</div>}
            {entry.status === "deleted" && <div className={styles.incompleteBadge}>Deleted</div>}
            {entry.status === "tracked" && <div className={styles.trackedBadge}>Tracked</div>}
          </>
        ) : mode === "history" ? (
          <div className={styles.exportedBadge}>Exported</div>
        ) : (
          isIncomplete(entry) && (
            <div className={styles.incompleteBadge}>Incomplete</div>
          )
        )}
        <div className={styles.unitsBadge}>
          {(entry.duration * 100) % 10 !== 0 ? entry.duration.toFixed(2) : entry.duration.toFixed(1)}
        </div>
      </div>
    </div>
  );

  const renderGroups = (groups: Record<string, Entry[]>) => {
    const keys = Object.keys(groups);
    return keys.map((k) => {
      const groupTotal = groups[k].reduce((sum, entry) => sum + (entry.duration || 0), 0);
      return (
        <div key={k}>
          {/* Group label with total (only show total when sorted by matter) */}
          <div className={styles.dateGroupHeader}>
            <div className={styles.dateText}>{k}</div>
            {sortingMode === "matter" && (
              <div className={styles.groupTotal}>{groupTotal.toFixed(1)} hrs</div>
            )}
          </div>
          {groups[k].map(renderRow)}
        </div>
      );
    });
  };

  return (
    <>
      {renderHeader()}
      <div className={styles.scrollContainer}>
        {entries.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyText}>No transactions to see here...</div>
          </div>
        ) : (
          renderGroups(groupedEntries)
        )}
      </div>
    </>
  );
};

export default EntriesTable;
